import { Anchor, Box, Button, LoadingOverlay, Paper, Stack, Text, Title } from "@mantine/core";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { AuthApi, OtpApi, PasswordApi } from "../../apis";
import { ReactComponent as EraLogo } from "../../assets/logo.svg";
import { Form } from "../../components/form";
import { PasswordField } from "../../components/form/password-field";
import { TextField } from "../../components/form/text-field";
import { authChange } from "../../store/Auth";
import { Message } from "../../utils/message";
import classes from "./auth-split.module.css";

const emailSchema = yup.object({
  email: yup.string().email("Имэйл хаяг буруу байна").required("Имэйл хаягаа оруулна уу").trim(),
});

const otpSchema = yup.object({
  otp: yup.string().required("Баталгаажуулах кодоо оруулна уу").trim(),
});

const passwordSchema = yup.object({
  password: yup.string().min(6, "Багадаа 6 тэмдэгттэй байх ёстой").max(16, "Ихдээ 16 тэмдэгттэй байх ёстой").required("Нууц үгээ оруулна уу"),
  checkPassword: yup
    .string()
    .min(6, "Багадаа 6 тэмдэгттэй байх ёстой")
    .max(16, "Ихдээ 16 тэмдэгттэй байх ёстой")
    .required("Нууц үгээ давтан оруулна уу")
    .oneOf([yup.ref("password")], "Нууц үг таарахгүй байна"),
});

export default function Forgot() {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [done, setDone] = React.useState<boolean>(false);
  const { sessionScope } = useSelector((state: any) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // The reset chain stores a scoped session token after each backend step (FORGOT then
  // CHANGE_PASSWORD). Dispatching authChange remounts the app tree, so the active step is
  // derived from the persisted sessionScope rather than local state — only the terminal
  // "done" flag is local (no token change follows password/change, so no remount).
  const step: "email" | "otp" | "password" | "done" = done
    ? "done"
    : sessionScope === "CHANGE_PASSWORD"
      ? "password"
      : sessionScope === "FORGOT"
        ? "otp"
        : "email";

  const onEmail = async (values: { email: string }) => {
    setLoading(true);
    try {
      const res = await AuthApi.forgot({ email: values.email });
      if (res?.message) Message.success(res.message);
      // Only a real, registered account yields a FORGOT-scope session to advance with;
      // the backend keeps the same anti-enumeration message either way.
      if (res?.accessToken) dispatch(authChange(res));
    } catch (error: any) {
      Message.error(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const onOtp = async (values: { otp: string }) => {
    setLoading(true);
    try {
      const res = await OtpApi.verify({ otpMethod: "FORGOT", otpCode: values.otp });
      Message.success("Код баталгаажлаа");
      dispatch(authChange(res));
    } catch (error: any) {
      Message.error(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const onPassword = async (values: { password: string }) => {
    setLoading(true);
    try {
      await PasswordApi.change({ password: values.password });
      Message.success("Нууц үг шинэчлэгдлээ");
      setDone(true);
    } catch (error: any) {
      Message.error(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const backToLogin = () => {
    dispatch(authChange());
    navigate("/auth/login");
  };

  const helper =
    step === "otp"
      ? "Имэйл хаягт ирсэн баталгаажуулах кодоо оруулна уу."
      : step === "password"
        ? "Шинэ нууц үгээ үүсгэнэ үү."
        : "Бүртгэлтэй имэйл хаягаа оруулбал сэргээх кодыг илгээнэ.";

  return (
    <Box className={classes.root}>
      <Box className={classes.brand}>
        <Box className={classes.brandMark}>
          <EraLogo className={classes.brandLogo} />
          <Box>
            <Text className={classes.brandName}>DeHUB</Text>
            <Text className={classes.brandSub}>платформ</Text>
          </Box>
        </Box>

        <Box className={classes.brandCopy}>
          <Text className={classes.eyebrow}>Dehub.MN платформын Back Office</Text>
          <Title order={1} className={classes.welcome}>
            Тавтай морилно уу!
          </Title>
          <Text className={classes.description}>
            DeHUB платформд бүртгэлтэй компаниудын бүртгэл, хяналт, гэрээний харилцаа болон админ эрхийн тохиргоо хийх, платформ дээр ашиглагдах лавлах
            мэдээллүүдийг бүртгэх, тохируулах, дотоод хэрэглэгчдийн бүртгэл, эрхийн тохиргоо
          </Text>
        </Box>
      </Box>

      <Box className={classes.form}>
        <Paper withBorder shadow="md" radius="lg" pos="relative" className={classes.card}>
          <LoadingOverlay visible={loading} />
          <Stack gap="lg">
            <Box className={classes.cardLogo}>
              <EraLogo className={classes.cardLogoMark} />
              <Box>
                <Text className={classes.cardLogoName}>DeHUB</Text>
                <Text className={classes.cardLogoSub}>платформ</Text>
              </Box>
            </Box>

            <Box>
              <Title order={2} fw={600} c="dark.6">
                НУУЦ ҮГ СЭРГЭЭХ
              </Title>
              {step !== "done" && (
                <Text size="sm" c="dimmed" mt={4}>
                  {helper}
                </Text>
              )}
            </Box>

            {step === "email" && (
              <Form initialValues={{ email: undefined }} onSubmit={onEmail} validationSchema={emailSchema}>
                {() => {
                  return (
                    <Stack gap="md">
                      <TextField label="Имэйл" required variant="default" radius="md" size="md" name="email" placeholder="ba***@mail.com" />

                      <Text size="sm" c="dimmed" ta="right">
                        Бүртгэлтэй бол{" "}
                        <Anchor component={Link} to="/auth/login" fw={600} c="brand">
                          Нэвтрэх
                        </Anchor>
                      </Text>

                      <Button type="submit" color="brand" size="md" radius="md" fullWidth mt={4}>
                        Илгээх
                      </Button>
                    </Stack>
                  );
                }}
              </Form>
            )}

            {step === "otp" && (
              <Form initialValues={{ otp: undefined }} onSubmit={onOtp} validationSchema={otpSchema}>
                {() => {
                  return (
                    <Stack gap="md">
                      <TextField label="Баталгаажуулах код" required variant="default" radius="md" size="md" name="otp" placeholder="Кодоо оруулна уу" />

                      <Button type="submit" color="brand" size="md" radius="md" fullWidth mt={4}>
                        Баталгаажуулах
                      </Button>

                      <Anchor component="button" type="button" onClick={backToLogin} c="dimmed" ta="center" size="sm">
                        Нэвтрэх хуудас руу буцах
                      </Anchor>
                    </Stack>
                  );
                }}
              </Form>
            )}

            {step === "password" && (
              <Form initialValues={{ password: undefined, checkPassword: undefined }} onSubmit={onPassword} validationSchema={passwordSchema}>
                {() => {
                  return (
                    <Stack gap="md">
                      <PasswordField label="Шинэ нууц үг" variant="default" radius="md" size="md" name="password" placeholder="Шинэ нууц үг" />
                      <PasswordField label="Нууц үг давтах" variant="default" radius="md" size="md" name="checkPassword" placeholder="Нууц үг давтах" />

                      <Button type="submit" color="brand" size="md" radius="md" fullWidth mt={4}>
                        Хадгалах
                      </Button>
                    </Stack>
                  );
                }}
              </Form>
            )}

            {step === "done" && (
              <Stack gap="lg">
                <Text ta="center" c="dark.6" fw={500}>
                  Таны нууц үг амжилттай шинэчлэгдлээ. Шинэ нууц үгээрээ нэвтэрнэ үү.
                </Text>
                <Button onClick={backToLogin} color="brand" size="md" radius="md" fullWidth>
                  Нэвтрэх хуудас руу буцах
                </Button>
              </Stack>
            )}
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
